/* eslint-disable max-statements */
/* eslint-disable max-len */

import { Anchor, Box, Button, Checkbox, Divider, Flex, Group, Loader, LoadingOverlay, Text, Title } from '@mantine/core';
import { useEffect, useRef, useState } from 'react';

import { Declaration, DeclarationGoods, DutiesAndTaxesTaxType, EnvironmentCountries, IRiskAnalysisHSCodeNewValues, ISaveNotes } from '@e-origin/shared';

import { notesIcon } from '@assets';
import { TextInput } from '@components';
import { RISK_DATE_ISO_SLICE_LIMIT, RISK_HS_CODE_PREFIX_LENGTH } from '@constants';
import { useDeclarant } from '@hooks/declarant';
import { useSettings } from '@hooks/useSettings';
import { toDecimal } from '@utils';
import { riskAnalysisService } from '../../../services';
import { ValidateAllStateEnum } from '../RiskAnalysis';
import { HSCodeDescriptionField } from './HSCodeDescriptionField';
import { HSCodeRecommendations } from './HSCodeRecommendations';
import { ManageSupportingDocuments } from './ManageSupportingDocuments';
import classes from './RiskAnalysisHSCode.module.css';
import { RiskAnalysisNotes } from './RiskAnalysisNotes';

interface RiskAnalysisHSCodeProps {
  declaration: Declaration;
  goodDetailsOriginal: DeclarationGoods;
  goodDetails: DeclarationGoods;
  update: (obj: IRiskAnalysisHSCodeNewValues) => void;
  validate: ValidateAllStateEnum;
}

export const RiskAnalysisHSCode: React.FC<RiskAnalysisHSCodeProps> = ({ goodDetails, goodDetailsOriginal, declaration, update, validate }) => {
  const { data: declarant, isLoading } = useDeclarant();
  const { data: settings } = useSettings();

  const currentDate = new Date().toISOString().slice(0, RISK_DATE_ISO_SLICE_LIMIT).replace(/-/g, '');
  const hsCodeRemoteURL =
    settings?.country === EnvironmentCountries.NL
      ? `https://tarief.douane.nl/ite-tariff-public/#/taric/nomenclature/sbn?sd=${currentDate}&d=I&cc=${goodDetails.commodityCode.hsCode}&l=${declarant.language}&ql=${declarant.language} `
      : `https://eservices.minfin.fgov.be/extTariffBrowser/Measure?cnCode=${goodDetails.commodityCode.hsCode}&country=29422&date=${currentDate}&lang=${declarant.language}&page=1&trade=0`;

  const taricDescriptionList = goodDetails.evalMethodAiHsCode?.taricDescriptionList || [];
  const taricDescriptionListException = goodDetails.evalMethodAiHsCode?.taricDescriptionListException || [];
  const taricOtherMeasures = goodDetails.evalMethodAiHsCode?.taricOtherMeasures || [];

  const [validatedHSCodeCheck, setValidatedHSCodeCheck] = useState(!!goodDetails.evalMethodAiHsCode?.validatedHSCodeCheck);
  const validatedHSCodeCheckDisabled = riskAnalysisService.evalMethodAiHsCodeCheckDisabled(goodDetailsOriginal);
  const invoiceHSCode = goodDetails.commodityCode.hsCode || '';
  const [newHsCodeDescription, setNewHsCodeDescription] = useState(goodDetails.evalMethodAiHsCode?.suggestedHsCode || '');
  const suggestedHsCodeChanged = useRef<boolean>(false);

  const [nowMadbValue, setNowMadbValue] = useState(goodDetails.evalMethodStatisticalValue?.suggestedLowest?.madbAmountPerKg);
  const [newHsCodeDescriptionDutyRate, setNewHsCodeDescriptionDutyRate] = useState<string>(goodDetails.evalMethodAiHsCode.suggestedTaricRate);
  const [newTaricMadbLoading, setNewTaricMadbLoading] = useState(false);

  const [showNotes, setShowNotes] = useState(false);

  const [showSupportingDocuments, setShowSupportingDocuments] = useState(false);

  const [annotationNote, setAnnotationNote] = useState('');
  const [annotationCustomsDecision, setAnnotationCustomsDecision] = useState(false);

  useEffect(() => {
    setAnnotationNote(goodDetailsOriginal.evalMethodAiHsCode.annotationNote || '');
    setAnnotationCustomsDecision(!!goodDetailsOriginal.evalMethodAiHsCode.annotationCustomsDecision);
  }, [goodDetailsOriginal]);

  useEffect(() => {
    setNewHsCodeDescription(goodDetails.evalMethodAiHsCode?.suggestedHsCode || '');
    setValidatedHSCodeCheck(!!goodDetails.evalMethodAiHsCode?.validatedHSCodeCheck);
  }, [goodDetails]);

  useEffect(() => {
    if (validate === null || validate === ValidateAllStateEnum.SOME_SELECTED) {
      return;
    }
    if (validatedHSCodeCheck || validate === ValidateAllStateEnum.ALL_SELECTED) {
      setNewHsCodeDescription('');
    }
    setValidatedHSCodeCheck(validate === ValidateAllStateEnum.ALL_SELECTED);
  }, [validate]);

  useEffect(() => {
    suggestedHsCodeChanged.current = newHsCodeDescription !== undefined && newHsCodeDescription !== goodDetails.evalMethodAiHsCode.suggestedHsCode;
  }, [newHsCodeDescription]);

  useEffect(() => {
    update({
      newHsCodeDescription,
      validatedHSCodeCheck,
      validatedHSCodeCheckDisabled,
      nowMadbValue,
      annotationNote,
      annotationCustomsDecision,
      newHsCodeDescriptionDutyRate,
    });
  }, [newHsCodeDescription, validatedHSCodeCheck, nowMadbValue, annotationNote, annotationCustomsDecision, newHsCodeDescriptionDutyRate]);

  const displayRateMessage = () => {
    if (goodDetails.evalMethodAiHsCode?.antiDumping) {
      return {
        badgeColor: 'red',
        badgeMessage: 'Anti dumping',
      };
    }

    return goodDetails.evalMethodAiHsCode?.hsCodeOk
      ? { badgeColor: 'green', badgeMessage: `${invoiceHSCode.slice(0, RISK_HS_CODE_PREFIX_LENGTH)} reliable` }
      : { badgeColor: 'red', badgeMessage: `${invoiceHSCode.slice(0, RISK_HS_CODE_PREFIX_LENGTH)} not reliable` };
  };

  const setNewHsCodeDescriptionDutyRateAndMadb = async () => {
    setNewTaricMadbLoading(true);
    try {
      const { dutyRate, madbValue } = await riskAnalysisService.retrieveDutyRateAndMadbValue(newHsCodeDescription);
      suggestedHsCodeChanged.current = false;
      setNewHsCodeDescriptionDutyRate(dutyRate);
      setNowMadbValue(madbValue[0]?.value);
    } catch (err) {
      // pass
    }
    setNewTaricMadbLoading(false);
  };

  const toggleManageSupportingDocumentsModal = () => {
    setShowSupportingDocuments(!showSupportingDocuments);
  };

  if (!declarant) {
    return <LoadingOverlay visible={isLoading} overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  return (
    <>
      {showNotes && (
        <RiskAnalysisNotes
          opened={showNotes}
          onHide={(res: ISaveNotes) => {
            setShowNotes(!showNotes);
            if (res) {
              setAnnotationCustomsDecision(res?.customsDecisionChecked);
              setAnnotationNote(res?.notes);
            }
          }}
          declaration={declaration}
          goodDetails={goodDetails}
        />
      )}
      {showSupportingDocuments && (
        <ManageSupportingDocuments
          opened={showSupportingDocuments}
          declaration={declaration}
          taricOtherMeasures={goodDetails.evalMethodAiHsCode.taricOtherMeasures}
          documents={declaration.documents.supportingDocuments}
          onHide={toggleManageSupportingDocumentsModal}
        ></ManageSupportingDocuments>
      )}
      <>
        <Group justify="space-between" align="center" px="md">
          <Group>
            <Button variant="outline" size="xs" color="gray" onClick={() => setShowNotes(true)} px="xs">
              <img src={notesIcon} />
            </Button>
            <Title order={6} fw={600}>
              HS Code
            </Title>
          </Group>
          <Checkbox
            checked={validatedHSCodeCheck}
            onChange={(event) => {
              setNewHsCodeDescription('');
              setValidatedHSCodeCheck(event.currentTarget.checked);
            }}
            disabled={validatedHSCodeCheckDisabled}
            size="sm"
            label="Validated HS Code"
          />
        </Group>

        <Divider mt="sm" mb="lg" />

        <Box px="lg">
          <div className={classes.formRow}>
            <Box className={classes.inputContainer}>
              <TextInput data-pw="invoice-hscode-input" size="lg" label="Invoice HS Code" value={invoiceHSCode} disabled {...displayRateMessage()} />
              <Text size="sm" c="blue" lh="xs" mt="sm">
                Duty rate types
              </Text>
              <Group mt="sm">
                {goodDetails.dutiesAndTaxes.items.map(({ taxType, taxBase }) => {
                  if (isNaN(taxBase[0].taxRate)) {
                    return;
                  }

                  const parsedTaxType = [DutiesAndTaxesTaxType.DUTY_A0, DutiesAndTaxesTaxType.VAT_B0].includes(taxType as DutiesAndTaxesTaxType)
                    ? taxType + '0'
                    : taxType;
                  return (
                    <Text key={parsedTaxType} size="sm" c="blue" lh="xs">
                      {parsedTaxType}: {toDecimal(taxBase[0].taxRate * 100, 2)}%
                    </Text>
                  );
                })}
              </Group>
            </Box>

            {!validatedHSCodeCheck && (
              <Box className={classes.inputContainer}>
                <TextInput
                  data-pw="new-hsCode-input"
                  size="lg"
                  label="New HS Code proposition"
                  value={newHsCodeDescription}
                  onChange={(event) => {
                    setNewHsCodeDescriptionDutyRate(null);
                    setNowMadbValue(event.currentTarget.value ? goodDetails.evalMethodStatisticalValue?.suggestedLowest?.madbAmountPerKg : null);
                    setNewHsCodeDescription(event.currentTarget.value);
                  }}
                />
                {newHsCodeDescription && (
                  <Box
                    data-pw="new-hsCode-info"
                    className={`${classes.riskAnalysisHSCodeInfo} ${
                      !goodDetails.evalMethodStatisticalValue?.suggestedLowest?.madbAmountPerKg && suggestedHsCodeChanged.current
                        ? classes.link
                        : classes.nonLink
                    } ${newHsCodeDescriptionDutyRate && !goodDetails.evalMethodStatisticalValue?.suggestedLowest?.madbAmountPerKg ? classes.cursorGrab : ''}`}
                    onClick={async () =>
                      !newHsCodeDescriptionDutyRate &&
                      !goodDetails.evalMethodStatisticalValue?.suggestedLowest?.madbAmountPerKg &&
                      setNewHsCodeDescriptionDutyRateAndMadb()
                    }
                  >
                    {!newHsCodeDescriptionDutyRate &&
                    !goodDetails.evalMethodStatisticalValue?.suggestedLowest?.madbAmountPerKg &&
                    !suggestedHsCodeChanged.current
                      ? `Not found`
                      : newHsCodeDescriptionDutyRate
                        ? `${newHsCodeDescriptionDutyRate} Third country duty`
                        : 'View third country duty & update IMADB'}
                    {newTaricMadbLoading && <Loader />}
                  </Box>
                )}
              </Box>
            )}
          </div>
          <Box className={classes.hsCodeDescriptionInfo}>
            <Box>
              <Flex justify="space-between" align="center" mb={8}>
                <Text fw={600} fz={14} mb={5}>
                  Invoice HS Code Description:
                </Text>
                <Button onClick={toggleManageSupportingDocumentsModal} type="button" className={classes.supportingDocumentsButton} variant="filled" size="lg">
                  Selected Codes
                </Button>
              </Flex>
              <HSCodeDescriptionField
                taricDescriptionList={taricDescriptionList}
                taricDescriptionListException={taricDescriptionListException}
                taricOtherMeasures={taricOtherMeasures}
              />
            </Box>
            <HSCodeRecommendations declarationId={declaration?._id} goodDetailsId={goodDetails?._id}></HSCodeRecommendations>
          </Box>
          <Flex justify="space-between" mt={10}>
            <Anchor href={hsCodeRemoteURL} target={'_blank'}>
              Tarbel HS Code website
            </Anchor>
          </Flex>
        </Box>
      </>
    </>
  );
};
